<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { useAuthStore } from '@/store/AuthStore'
import { authApi } from '@/api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AuthField from '@/components/auth/AuthField.vue'
import { useGoogleAuth } from '@/composables/useGoogleAuth'
import { useAnalytics } from '@/composables/useAnalytics'
import { getErrorStatus } from '@/utils/error'
import { getUtmParams, getSourceString } from '@/utils/utm'

const { capture } = useAnalytics()

const email = ref('')
const password = ref('')
const isLoading = ref(false)
const errorMessage = ref('')

const router = useRouter()
const auth = useAuthStore()

const googleAuth = useGoogleAuth()
const isGoogleLoading = ref(false)

const isFormValid = computed(() => email.value.includes('@') && password.value.length >= 6)

onMounted(() => {
  capture('signin_viewed')
  googleAuth.initialize(handleGoogleCredential)
  googleAuth.registerButtonContainer('google-signin-button')
})

function onGoogleSignIn() {
  capture('signin_method_selected', { method: 'google' })
  capture('signin_submitted')
  googleAuth.signIn()
}

async function handleGoogleCredential(idToken: string) {
  isGoogleLoading.value = true
  errorMessage.value = ''
  try {
    const source = getSourceString()
    await authApi.authGooglePost({ idToken, ...(source ? { source } : {}) } as any)
    await auth.onAuthenticated()
    capture('signin_succeeded', { method: 'google' })
    capture('logged_in', { method: 'google', ...getUtmParams() })
    router.push('/')
  } catch (e: unknown) {
    console.error('Google login failed:', e)
    capture('signin_failed')
    errorMessage.value =
      getErrorStatus(e) === 401
        ? 'Google authentication failed. Please try again.'
        : 'Unable to sign in with Google. Please try again.'
  } finally {
    isGoogleLoading.value = false
  }
}

async function handleLogin() {
  if (!isFormValid.value) return
  errorMessage.value = ''
  isLoading.value = true
  capture('signin_method_selected', { method: 'email' })
  capture('signin_submitted')
  try {
    const source = getSourceString()
    await authApi.authLoginPost({
      email: email.value,
      password: password.value,
      ...(source ? { source } : {}),
    } as any)
    await auth.onAuthenticated()
    capture('signin_succeeded', { method: 'email' })
    capture('logged_in', { method: 'email', ...getUtmParams() })
    router.push('/')
  } catch (e: unknown) {
    console.error('Login failed:', e)
    capture('signin_failed')
    const status = getErrorStatus(e)
    if (status === 401) errorMessage.value = 'Invalid email or password. Please try again.'
    else if (status === 429) errorMessage.value = 'Too many attempts. Please wait a moment and try again.'
    else errorMessage.value = 'Unable to sign in. Please check your connection and try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="w-full max-w-md">
    <div class="text-center mb-7">
      <h1 class="text-2xl font-bold text-ink-900">Welcome back</h1>
      <p class="text-ink-500 mt-1">Sign in to your account</p>
    </div>

    <div
      v-if="errorMessage"
      class="mb-4 p-4 bg-danger-soft border border-danger-ink/15 rounded-xl flex items-start gap-3"
    >
      <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-danger-ink mt-0.5" />
      <p class="text-sm text-danger-ink">{{ errorMessage }}</p>
    </div>

    <div class="space-y-3">
      <div id="google-signin-button" class="h-0 overflow-hidden"></div>
      <button
        type="button"
        :disabled="!googleAuth.isInitialized.value || isGoogleLoading"
        class="w-full h-[52px] inline-flex items-center justify-center gap-2.5 rounded-xl bg-surface border border-ink-200 text-ink-900 font-semibold hover:bg-ink-50 disabled:opacity-60 transition-colors"
        @click="onGoogleSignIn"
      >
        <font-awesome-icon :icon="['fab', 'google']" class="text-lg" />
        Continue with Google
      </button>
      <p v-if="googleAuth.error.value" class="text-sm text-danger-ink text-center">
        {{ googleAuth.error.value }}
      </p>

      <div class="flex items-center gap-3 py-1">
        <div class="flex-1 border-t border-ink-200"></div>
        <span class="text-[13px] font-semibold text-ink-500">or sign in with email</span>
        <div class="flex-1 border-t border-ink-200"></div>
      </div>
    </div>

    <form class="space-y-4" @submit.prevent="handleLogin">
      <AuthField
        v-model="email"
        label="EMAIL ADDRESS"
        :icon="['fas', 'envelope']"
        type="email"
        autocomplete="email"
        required
        placeholder="you@school.com"
        :error="!!errorMessage"
      />
      <AuthField
        v-model="password"
        label="PASSWORD"
        :icon="['fas', 'lock']"
        type="password"
        autocomplete="current-password"
        required
        placeholder="Enter your password"
        :error="!!errorMessage"
      />

      <div class="flex justify-end">
        <RouterLink
          to="/forgot-password"
          class="text-sm font-medium text-[var(--primary)] hover:opacity-80"
          @click="capture('forgot_password_tapped')"
        >
          Forgot password?
        </RouterLink>
      </div>

      <button
        type="submit"
        :disabled="!isFormValid || isLoading"
        class="w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 disabled:opacity-50 transition-colors"
      >
        {{ isLoading ? 'Signing in…' : 'Sign in' }}
      </button>
    </form>

    <p class="mt-7 text-center text-sm text-ink-600">
      Don't have an account?
      <RouterLink to="/register" class="font-semibold text-ink-900 hover:opacity-80">Get started free</RouterLink>
    </p>
  </div>
</template>
